import type { EditableFloorPlan, MultiPolygonCoordinates } from '../models/editor-feature';

/**
 * Working out roughly how many drawing units make a metre, when nobody has measured.
 *
 * Every stored plan's `unitsPerMetre` is null: these coordinates are each project's own
 * arbitrary units, and nothing in the file says what they mean. That leaves every
 * metre-denominated feature — desk sizes, group spacing, grid snapping, area readouts —
 * switched off on every real floorplan until an administrator measures a known dimension.
 *
 * So the editor estimates one. The estimate is deliberately kept out of the document: it is
 * never saved, never sent, and the moment a real measurement exists it is discarded. What it
 * buys is a tool that works on first open, with the figure shown as an estimate so nobody
 * mistakes it for a survey.
 *
 * Two bases, in order of how much they can be trusted.
 */

export type ScaleBasis = 'deskShapes' | 'planExtent';

export interface ScaleEstimate {
  readonly unitsPerMetre: number;
  readonly basis: ScaleBasis;
  /** How many features the estimate was drawn from. One desk is a guess; forty is a pattern. */
  readonly sampleSize: number;
}

/**
 * A catalogue desk, which is also what the backend's desk detector looks for.
 *
 * The long side rather than the area: desks vary far more in depth than in width, and a
 * corner desk's footprint is nearly double a straight one's while its front edge is the same.
 */
const DESK_WIDTH_METRES = 1.6;

/**
 * A floor plate's longer side, for a plan with no furniture to measure against.
 *
 * Far cruder than the desk basis and only ever a starting point. It is here because the
 * alternative for an empty or freshly imported plan is no scale at all, and a grid spacing
 * and desk size that are in the right order of magnitude beat nothing to draw with.
 */
const TYPICAL_FLOOR_METRES = 50;

const longSideOf = (coordinates: MultiPolygonCoordinates): number => {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  coordinates.forEach((polygon) => polygon.forEach((ring) => ring.forEach(([x, y]) => {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  })));

  return Number.isFinite(minX) ? Math.max(maxX - minX, maxY - minY) : 0;
};

/**
 * The middle value, not the mean.
 *
 * One desk shape drawn at the wrong size — or one stray polygon the tagger called furniture —
 * drags an average by as much as it likes and a median not at all.
 */
const medianOf = (values: readonly number[]): number => {
  const sorted = [...values].sort((first, second) => first - second);
  const middle = Math.floor(sorted.length / 2);

  return sorted.length % 2 === 0
    ? (sorted[middle - 1] + sorted[middle]) / 2
    : sorted[middle];
};

export const estimateScale = (floorPlan: EditableFloorPlan | undefined): ScaleEstimate | null => {
  if (!floorPlan) {
    return null;
  }

  const deskSides = floorPlan.deskShapes
    .map((shape) => longSideOf(shape.coordinates))
    .filter((side) => side > 0);

  if (deskSides.length) {
    return {
      unitsPerMetre: medianOf(deskSides) / DESK_WIDTH_METRES,
      basis: 'deskShapes',
      sampleSize: deskSides.length,
    };
  }

  const outlines = [...floorPlan.border, ...floorPlan.rooms, ...floorPlan.floorBackdrop];
  const extent = Math.max(0, ...outlines.map((feature) => longSideOf(feature.coordinates)));

  if (extent > 0) {
    return { unitsPerMetre: extent / TYPICAL_FLOOR_METRES, basis: 'planExtent', sampleSize: outlines.length };
  }

  // Nothing drawn. There is no dimension to take a ratio from, and inventing one would be a
  // number with nothing behind it at all.
  return null;
};
